import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Stock from './pages/Stock';
import Orders from './pages/Orders';
import Login from './pages/Login';
import Profile from './pages/Profile';
import { CartProvider } from './components/CartContext';
import { motion } from 'framer-motion';
import CryptoJS from 'crypto-js'; // Para desencriptar token al cargar

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null); // Almacena el objeto usuario completo
  const [authToken, setAuthToken] = useState(null); // Almacena el token desencriptado

  // Clave secreta para desencriptar (¡debe coincidir con la de Login!)
  const SECRET_KEY = 'mi-clave-secreta-massi-store-2024';

  useEffect(() => {
    // Al cargar la app, verifica si hay token encriptado y datos de usuario
    const encryptedToken = localStorage.getItem('encryptedToken');
    const userDataStr = localStorage.getItem('userData');

    if (encryptedToken && userDataStr) {
      try {
        // Desencripta el token
        const decryptedToken = CryptoJS.AES.decrypt(encryptedToken, SECRET_KEY).toString(CryptoJS.enc.Utf8);
        const userData = JSON.parse(userDataStr);

        if (decryptedToken && userData) {
          setAuthToken(decryptedToken);
          setCurrentUser(userData);
          setIsAuthenticated(true);
        } else {
          // Token inválido, limpia
          localStorage.removeItem('encryptedToken');
          localStorage.removeItem('userData');
        }
      } catch (err) {
        // Error al desencriptar, limpia
        console.error('Error al desencriptar token:', err);
        localStorage.removeItem('encryptedToken');
        localStorage.removeItem('userData');
      }
    }
  }, []);

  const handleLogin = (success, user, token) => {
    setIsAuthenticated(success);
    if (success) {
      setCurrentUser(user); // Usuario completo con todos los campos
      setAuthToken(token); // Token desencriptado
    } else {
      setCurrentUser(null);
      setAuthToken(null);
      // Limpia localStorage en caso de error
      localStorage.removeItem('encryptedToken');
      localStorage.removeItem('userData');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    setAuthToken(null);
    // Limpia localStorage de forma segura
    localStorage.removeItem('encryptedToken');
    localStorage.removeItem('userData');
  };

  // Nombre para mostrar en Navbar (ej: nomPrimero + apePaterno)
  const userName = currentUser ? `${currentUser.nomPrimero} ${currentUser.apePaterno}` : null;

  return (
    <Router>
      <CartProvider>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
          {isAuthenticated && <Navbar onLogout={handleLogout} userName={userName} currentUser={currentUser} token={authToken} />}
          <motion.main
            className="pt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <Routes>
              <Route path="/login" element={<Login onLogin={handleLogin} />} />
              {/* Rutas protegidas - pasa currentUser y token si es necesario */}
              <Route
                path="/"
                element={isAuthenticated ? <Dashboard currentUser={currentUser} token={authToken} /> : <Navigate to="/login" replace />}
              />
              <Route
                path="/clients"
                element={isAuthenticated ? <Clients currentUser={currentUser} token={authToken} /> : <Navigate to="/login" replace />}
              />
              <Route
                path="/stock"
                element={isAuthenticated ? <Stock currentUser={currentUser} token={authToken} /> : <Navigate to="/login" replace />}
              />
              <Route
                path="/orders"
                element={isAuthenticated ? <Orders currentUser={currentUser} token={authToken} /> : <Navigate to="/login" replace />}
              />
              <Route
                path="/profile"
                element={isAuthenticated ? <Profile currentUser={currentUser} token={authToken} /> : <Navigate to="/login" replace />}
              />
            </Routes>
          </motion.main>
        </div>
      </CartProvider>
    </Router>
  );
};

export default App;